import { FC } from 'react';

import { useTranslate } from 'hooks/useTranslate';
import { Operation } from 'types';

import { formatCurrencyDecimals, formatNumber } from 'utils/formats';

import TableRow from 'modules/_shared/Table/components/TableRow';
import CellData from 'modules/_shared/components/Tables/CellData';
import { RowCell } from 'modules/_shared/Table/types/RowCell';
import { getOperationAmount } from 'modules/transactions/utils/getOperationAmount';

import { format } from 'date-fns';
import ToggleMenu from 'modules/_shared/components/ToggleMenu';
import { getOperationSharesCount } from 'modules/transactions/utils/getOperationSharesCount';
import { useOperationsListPage } from '../context/OperationsListPageContext';

type OperationsTableRowProps = {
  index: number;
  operation: Operation;
};

const OperationsTableRow: FC<OperationsTableRowProps> = ({
  index,
  operation,
}) => {
  const { t } = useTranslate();

  const { data, actions } = useOperationsListPage();

  const hasOptions = data.isAdmin && !data.isReadOnly;

  const handleEdit = () => actions.handleEdit(index);

  const toggleOptions = [
    <a
      className="d-flex cursor-pointer"
      key={`operation-edit-option-${index}`}
      onClick={handleEdit}
    >
      <em className="icon ni ni-edit fs-20px" />
      <span className="ml-1">{t('Edit')}</span>
    </a>,
  ];

  const shares = getOperationSharesCount(operation);
  const amount = data.society ? getOperationAmount(operation, data.society) : 0;

  const cells = [
    {
      key: `type-${index}`,
      content: CellData.OperationType({
        operation,
        societyId: data.society?._id || '',
      }),
    },

    {
      key: `comments-${index}`,
      content: operation.comments || '-',
      hideOnMobile: true,
    },

    {
      key: `date-${index}`,
      content: format(new Date(operation.date), 'dd/MM/yyyy'),
      textPosition: 'center',
    },

    {
      key: `shares-${index}`,
      content: (
        <span className="text-primary">{formatNumber(shares) || '-'}</span>
      ),
      textPosition: 'right',
    },
    {
      key: `amount-${index}`,
      content: formatCurrencyDecimals(amount) || '-',
      textPosition: 'right',
      hideOnMobile: true,
    },
    hasOptions
      ? {
          key: `holding-class-options-${index}`,
          content: <ToggleMenu toggleOptions={toggleOptions} />,
          textPosition: 'center',
        }
      : null,
  ].filter(Boolean) as RowCell[];

  return <TableRow cells={cells} />;
};

export default OperationsTableRow;
